import React from 'react';
import { ThemeProvider } from './context/ThemeContext';
import Header from './components/Header';
import IntroductionCard from './components/IntroductionCard';
import ControlPanel from './components/ControlPanel';
import VisualizationContainer from './components/VisualizationContainer';
import { useDataState } from './hooks/useDataState';
import { useVisualizationState } from './hooks/useVisualizationState';
import { useComparisonState } from './hooks/useComparisonState';

const MedExpQAAnalysis = () => {
  const visualizationState = useVisualizationState();
  const comparisonState = useComparisonState();

  const dataState = useDataState({
    setSelectedMethod: visualizationState.setSelectedMethod,
    setSelectedModel: visualizationState.setSelectedModel,
    setComparisonModel: comparisonState.setComparisonModel,
    setSelectedMethodsForComparison: comparisonState.setSelectedMethodsForComparison
  });

  const {
    methodData,
    experimentsMetadata,
    fileInfo,
    isLoading,
    error,
    availableSettings
  } = dataState;

  const {
    selectedModel,
    setSelectedModel,
    viewType,
    setViewType,
    selectedMethod,
    setSelectedMethod,
    selectedLanguages,
    setSelectedLanguages,
    selectedSettings,
    setSelectedSettings,
    heatmapMetric,
    setHeatmapMetric,
    statisticalView,
    setStatisticalView
  } = visualizationState;

  const {
    selectedMethodsForComparison,
    setSelectedMethodsForComparison,
    comparisonModel,
    setComparisonModel
  } = comparisonState;

  const hasData = Object.keys(methodData).length > 0;

  return (
    <div className="min-h-screen font-sans" style={{ color: 'var(--text-primary)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Header />

        <IntroductionCard />

        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', boxShadow: 'var(--shadow-md)' }}>
              <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary-500)', borderTopColor: 'transparent' }} />
              <span className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Loading data...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-xl status-error">
            <p className="font-medium">{error}</p>
          </div>
        )}

        {hasData && (
          <>
            <ControlPanel
              fileInfo={fileInfo}
              selectedMethod={selectedMethod}
              setSelectedMethod={setSelectedMethod}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              viewType={viewType}
              setViewType={setViewType}
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
              selectedSettings={selectedSettings}
              setSelectedSettings={setSelectedSettings}
              selectedMethodsForComparison={selectedMethodsForComparison}
              setSelectedMethodsForComparison={setSelectedMethodsForComparison}
              comparisonModel={comparisonModel}
              setComparisonModel={setComparisonModel}
              heatmapMetric={heatmapMetric}
              setHeatmapMetric={setHeatmapMetric}
              statisticalView={statisticalView}
              setStatisticalView={setStatisticalView}
              methodData={methodData}
              experimentsMetadata={experimentsMetadata}
              availableSettings={availableSettings}
            />

            <VisualizationContainer
              viewType={viewType}
              selectedMethod={selectedMethod}
              selectedModel={selectedModel}
              methodData={methodData}
              selectedLanguages={selectedLanguages}
              heatmapMetric={heatmapMetric}
              statisticalView={statisticalView}
              selectedMethodsForComparison={selectedMethodsForComparison}
              comparisonModel={comparisonModel}
              experimentsMetadata={experimentsMetadata}
            />
          </>
        )}

        {!hasData && !isLoading && !error && (
          <div className="text-center py-16">
            <div className="inline-block p-8 rounded-2xl" style={{ backgroundColor: 'var(--card-bg)', boxShadow: 'var(--shadow-lg)' }}>
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>No Data Available</h3>
              <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
                Data will be loaded automatically from the configured Google Sheets URL.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <MedExpQAAnalysis />
    </ThemeProvider>
  );
};

export default App;
